import { useEffect, useRef, useCallback } from 'react';

export const usePortfolioEffects = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const projectCardListenersRef = useRef<Array<{ card: Element; mouseenter: EventListener; mouseleave: EventListener }>>([]);

  const updateActiveDot = useCallback((sectionId: string) => {
    const dots = document.querySelectorAll('.dot');
    dots.forEach(dot => {
      dot.classList.remove('active');
      if (dot.getAttribute('href')?.includes(sectionId)) {
        dot.classList.add('active');
      }
    });
  }, []);

  useEffect(() => {
    // Smooth Scroll Handler
    const handleSmoothScroll = (e: Event) => {
      e.preventDefault();
      const target = (e.currentTarget as HTMLAnchorElement).getAttribute('href');
      if (target) {
        document.querySelector(target)?.scrollIntoView({ behavior: 'smooth' });
      }
    };

    // Cache DOM queries for better performance
    const sections = document.querySelectorAll('section');
    const anchors = document.querySelectorAll('a[href^="#"]');
    const dots = document.querySelectorAll('.dot');
    const projectCards = document.querySelectorAll('.project-card');

    // Intersection Observer for section detection (replaces scroll event)
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.getAttribute('id');
            if (sectionId) {
              updateActiveDot(sectionId);
            }
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '-100px 0px 0px 0px'
      }
    );

    // Observe all sections
    sections.forEach(section => {
      observerRef.current?.observe(section);
    });

    // Project Card Hover with cleanup tracking
    projectCards.forEach(card => {
      const mouseenterHandler = () => card.classList.add('hovered');
      const mouseleaveHandler = () => card.classList.remove('hovered');

      card.addEventListener('mouseenter', mouseenterHandler);
      card.addEventListener('mouseleave', mouseleaveHandler);

      // Store for cleanup
      projectCardListenersRef.current.push({
        card,
        mouseenter: mouseenterHandler,
        mouseleave: mouseleaveHandler
      });
    });

    // Add smooth scroll listeners
    anchors.forEach(anchor => {
      anchor.addEventListener('click', handleSmoothScroll);
    });
    dots.forEach(dot => {
      dot.addEventListener('click', handleSmoothScroll);
    });

    // Cleanup function
    return () => {
      // Disconnect Intersection Observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // Remove project card listeners
      projectCardListenersRef.current.forEach(({ card, mouseenter, mouseleave }) => {
        card.removeEventListener('mouseenter', mouseenter);
        card.removeEventListener('mouseleave', mouseleave);
      });
      projectCardListenersRef.current = [];

      // Remove smooth scroll listeners
      anchors.forEach(anchor => {
        anchor.removeEventListener('click', handleSmoothScroll);
      });
      dots.forEach(dot => {
        dot.removeEventListener('click', handleSmoothScroll);
      });
    };
  }, [updateActiveDot]);
};